import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { removePortfolioFromCart } from 'api/operations/cartServiceV2';
import { getAllEnrollmentPeriodsByGroupId, saveProposalAndCompleteStep } from 'api/operations/enrollmentService';
import { getCoveredProductLines } from 'global/services-helpers/enrollmentService';
import { updateGroup } from 'api/operations/groupService';
import { getPortfoliosById } from 'api/operations/productServiceV2';
import { generateProposal } from 'api/operations/proposalServiceV3';
import { convertNewSaleCartIntoProductLine } from 'global/services-helpers/cartService';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { redirectToRoute } from 'routes/redirectToRoute';
import { RouteV2Id } from 'routes/routesConfigV2';
import QuoteFlowDelegate, { QuoteFlowDomainParams } from 'store/flows/quote/quoteFlowDelegate';
import { PortfolioToCartItem } from 'store/modules/proposal/types';
import ProposalPageStore from 'store/pages/proposal/pageStore';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { ProposalItemsStore } from 'store/ui-stores/proposal-v2/proposalItemsStore';
import { Cart } from 'types/cartServiceV2';
import { EnrollmentType, InstalledProductLine, Proposal, USER_ACTIONS } from 'types/enrollmentService';
import { AssigneeType, Group } from 'types/groupService';
import { UwGuideline } from 'types/uwService';
import {
  PRODUCT_LINES_CONTENT_MAP,
  ProductLineSectionType,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { bpHistory } from 'global/bpHistory';
import { isEnrollmentPeriodNotEditable } from 'global/checklists-helpers/ChecklistHelper';
import { AddQuoteValidationError, ProductLineSectionGroup } from './types';

export default class AddQuotePageStore {
  rootStore: RootStore = getRootStoreInstance();
  private _domainParams: QuoteFlowDomainParams;
  private _isActionLoading = false;
  private _coveredProductLineIds: ProductLineId[] = [];
  public flowDelegate: QuoteFlowDelegate;

  private get _proposalItemsStore(): ProposalItemsStore {
    return this.rootStore.proposalItemsStore;
  }

  private get _isChangePlanActionAvailable(): boolean {
    return this.rootStore.domainStoreV2.isChangePlanActionAvailable(this.rootStore.userStore.userRoles);
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  // Reused ProposalCards and modals still needs ProposalPageStore for ProductLineDelegate / CartItemDelegate.
  public get proposalPageStore(): ProposalPageStore {
    return this.rootStore.proposalPageStore;
  }

  public get cart(): Cart {
    return {
      ...this._proposalItemsStore.cart,
      cartItems: this._proposalItemsStore.cartItems,
    };
  }

  public get group(): Group {
    return this._proposalItemsStore.group;
  }

  public get proposal(): Proposal {
    return this._proposalItemsStore.proposal;
  }

  public get uwGuidelines(): UwGuideline[] {
    return this._proposalItemsStore.uwGuidelines;
  }

  /**
   * Add quote only builds the cart. Selecting and confirming happens on proposal v2.
   * Archived and sold periods stay locked. Expired quotes never reach this page from the group.
   */
  public get isQuoteEditable(): boolean {
    return !isEnrollmentPeriodNotEditable(this._proposalItemsStore.enrollmentPeriod.stage);
  }

  public get isMutableProposal(): boolean {
    return this._proposalItemsStore.isMutableProposal;
  }

  public get validationErrors(): AddQuoteValidationError[] {
    const { cartItems, productLines } = this._proposalItemsStore;

    const hasInstalledPlans = Boolean(
      this._proposalItemsStore.enrollmentPeriod.installedProposal?.productLines.some(
        (productLine) => productLine.products.length > 0,
      ),
    );

    if (cartItems.length === 0 && !hasInstalledPlans) {
      return [{ type: 'NO_PLANS' }];
    }

    const cartProductLineIds = new Set(cartItems.map((cartItem) => cartItem.productLineId));
    const notSelectedDependentPLines = productLines
      .filter(
        (productLine) =>
          productLine.dependentOnProductLineId &&
          cartProductLineIds.has(productLine.productLineId) &&
          !cartProductLineIds.has(productLine.dependentOnProductLineId),
      )
      .map((productLine) => productLine.dependentOnProductLineId)
      .filter((productLineId) => !!productLineId);

    if (notSelectedDependentPLines.length === 0) {
      return [];
    }

    return [
      {
        type: 'DEPENDENT_PRODUCT_LINES',
        productLineIds: [...new Set(notSelectedDependentPLines)],
      },
    ];
  }

  public get canCompleteStep(): boolean {
    return !this._isActionLoading && !this.flowDelegate.isInitialDataLoading && this.validationErrors.length === 0;
  }

  public get isRenewalEnrollment(): boolean {
    return this._proposalItemsStore.enrollmentPeriod.type === EnrollmentType.RENEWAL;
  }

  public getInstalledProductLine(productLineId: ProductLineId): InstalledProductLine | undefined {
    return this._proposalItemsStore.getInstalledProductLine(productLineId);
  }

  public isRenewalProductLine(productLineId: ProductLineId): boolean {
    return this._proposalItemsStore.isRenewalProductLine(productLineId);
  }

  public getCartItemsForProductLine(productLineId: ProductLineId): PortfolioToCartItem[] {
    return (
      this._proposalItemsStore.portfoliosByProductLine.find((item) => item.productLineId === productLineId)
        ?.portfolioToCartItems ?? []
    );
  }

  public get configurePlanModal(): {
    show: boolean;
    loading: boolean;
    form: FormObjectFromJSON;
    dynamicForm: formUtils.DynamicFormController | null;
  } {
    const modal = this.rootStore.portfolioConfigModalStore;

    return {
      show: modal.showConfigurePlanModal,
      loading: modal.loading,
      form: modal.form,
      dynamicForm: modal.dynamicForm,
    };
  }

  private get _productLineIdsWithPlans(): ProductLineId[] {
    const installedProductLineIds =
      this._proposalItemsStore.enrollmentPeriod.installedProposal?.productLines.map((productLine) => productLine.id) ??
      [];

    return [
      ...new Set([
        ...this._proposalItemsStore.cartItems.map((cartItem) => cartItem.productLineId),
        ...installedProductLineIds,
      ]),
    ];
  }

  // Browsing hides covered lines when Change Plan is off. Still show this period's installed line for the renewal card.
  private _isProductLineVisibleOnAddQuote(productLineId: ProductLineId): boolean {
    if (this.isRenewalProductLine(productLineId)) {
      return true;
    }

    return this._isChangePlanActionAvailable || !this._coveredProductLineIds.includes(productLineId);
  }

  /**
   * Add Plan. New sale: allowed when the quote is editable.
   * Renewal: Change Plan on an installed/covered line, Add Benefit on other lines.
   * Change Plan on implies Add Benefit on; Add Benefit can be on with Change Plan off.
   */
  public canAddPlan(productLineId: ProductLineId): boolean {
    if (!this.isQuoteEditable) {
      return false;
    }

    if (!this.isRenewalEnrollment) {
      return true;
    }

    if (this._coveredProductLineIds.includes(productLineId)) {
      return this._isChangePlanActionAvailable;
    }

    return this.rootStore.domainStoreV2.isAddBenefitActionAvailable;
  }

  /**
   * Remove a cart plan. Allowed when the quote is editable.
   * Remove button is not shown for renewal cards
   */
  public get canRemovePlan(): boolean {
    if (!this.isQuoteEditable) {
      return false;
    }
    return true;
  }

  public get productLineSections(): ProductLineSectionGroup[] {
    const availableProductLineIds = this._proposalItemsStore.productLines
      .filter(({ available, rateSegment }) => available && rateSegment === this._proposalItemsStore.cart.rateSegment)
      .map(({ productLineId }) => productLineId)
      .filter((productLineId) => this._isProductLineVisibleOnAddQuote(productLineId));

    return (
      Object.values(ProductLineSectionType)
        .map((section) => {
          // Get all available product lines that belong to the current section.
          const productLineIds = availableProductLineIds
            .filter((productLineId) => PRODUCT_LINES_CONTENT_MAP[productLineId]?.section === section)
            .sort(sortByProductLineId);

          return {
            section,
            productLineIds,
            productLineIdsWithPlans: productLineIds.filter((productLineId) =>
              this._productLineIdsWithPlans.includes(productLineId),
            ),
          };
        })
        // Do not return sections with no available product lines.
        .filter(({ productLineIds }) => productLineIds.length > 0)
    );
  }

  private _setActionLoading(isLoading: boolean): void {
    this._isActionLoading = isLoading;
  }

  public async getInitialData(): Promise<void> {
    const { groupId, periodId, cartId } = this._domainParams;

    this.flowDelegate.setIsInitialDataLoading(true);

    try {
      const [, , enrollmentPeriods] = await Promise.all([
        this.flowDelegate.getFlowInitialData(),
        this._proposalItemsStore.collectProposalData(groupId, cartId, periodId),
        getAllEnrollmentPeriodsByGroupId(groupId),
      ]);
      runInAction(() => {
        this._coveredProductLineIds = getCoveredProductLines(enrollmentPeriods || []);
        this._syncProposalProductLinesFromCart();
      });
    } finally {
      runInAction(() => {
        this.flowDelegate.setIsInitialDataLoading(false);
      });
    }
  }

  // Proposal cards still call getProductLineFromProposal; browsing can add cart lines before they exist on the proposal.
  private _syncProposalProductLinesFromCart(): void {
    const existingProductLineIds = new Set(
      this._proposalItemsStore.proposal.productLines.map((productLine) => productLine.id),
    );

    convertNewSaleCartIntoProductLine(this.cart).forEach((productLine) => {
      if (!existingProductLineIds.has(productLine.id)) {
        this._proposalItemsStore.proposal.productLines.push(productLine);
      }
    });
  }

  public onAddPlan(productLineId: ProductLineId): void {
    redirectToRoute(RouteV2Id.INITIATE_SHELF_BROWSING, {
      ...this._domainParams,
      productLineId,
    });
  }

  public get annualPremiumFormId(): string {
    const { domainConfig, cart } = this._proposalItemsStore;
    return domainConfig[cart.rateSegment]?.annualPremiumFormId || '';
  }

  public onOverrideRates(): void {
    redirectToRoute(RouteV2Id.QUOTE_OVERRIDE_RATES, this._domainParams);
  }

  public onEstimatedCost(): void {
    redirectToRoute(RouteV2Id.QUOTE_ESTIMATED_COST, this._domainParams);
  }

  public closeConfigurePlanModal(): void {
    this.rootStore.portfolioConfigModalStore.closeConfigurePlanModal();
  }

  private async _openConfigurePlanModal(portfolioId: string): Promise<void> {
    const portfolio = this._proposalItemsStore.getPortfolioById(portfolioId);
    if (!portfolio) {
      return;
    }

    const { id: cartId, lastUpdateBrowsingCriteria } = this._proposalItemsStore.cart;
    await this.rootStore.portfolioConfigModalStore.openConfigurePlanModal(
      portfolio,
      cartId,
      lastUpdateBrowsingCriteria.effectiveDate,
      this._proposalItemsStore.cartItems,
    );
  }

  public async savePortfolioConfigChanges(): Promise<void> {
    const modal = this.rootStore.portfolioConfigModalStore;
    const configurePortfolioId = modal.portfolioId;

    this._proposalItemsStore.setLoadingForPortfolio(configurePortfolioId, true);
    modal.closeConfigurePlanModal();

    const updatedCard = await modal.saveAnswers().catch(async (): Promise<null> => {
      this._proposalItemsStore.setLoadingForPortfolio(configurePortfolioId, false);
      await this._openConfigurePlanModal(configurePortfolioId);
      return null;
    });

    await runInAction(async () => {
      if (updatedCard?.data?.addPortfolioConfigToCart) {
        await this._proposalItemsStore.updateCartItem(updatedCard.data.addPortfolioConfigToCart, configurePortfolioId);
      }
    });
  }

  public async removePlan(portfolioId: string): Promise<void> {
    this._setActionLoading(true);

    try {
      const cartId = this._proposalItemsStore.cart.id;
      const res = await removePortfolioFromCart(cartId, portfolioId, false);
      const currentCart = res.data?.removePortfolioConfigFromCart;

      if (currentCart) {
        await this._proposalItemsStore.updateCustomPortfolioAndCart(currentCart, portfolioId, false);
      }
    } finally {
      runInAction(() => {
        this._setActionLoading(false);
      });
    }
  }

  public async onComplete(): Promise<void> {
    this._setActionLoading(true);

    const cart = this.cart;
    const group = this.group;
    const inputProductLines = this.proposal.productLines;
    const inputGroup = buildInputGroupFromGroup(group);
    const {
      lastUpdateBrowsingCriteria: { zipCode: zip, state, county, effectiveDate },
      groupId,
      cartName,
      id: cartId,
      rateSegment,
    } = cart;

    inputGroup.contactInfo.primaryAddress = {
      ...inputGroup.contactInfo.primaryAddress,
      zip,
      state,
      county,
    };

    try {
      const [savedPeriod, portfolios] = await Promise.all([
        saveProposalAndCompleteStep(
          {
            groupId,
            cartName,
            cartId,
            effectiveDate,
            productLines: inputProductLines,
          },
          this._domainParams.periodId,
          USER_ACTIONS.COMPLETE_ADD_QUOTE,
          rateSegment,
        ),
        getPortfoliosById(cart.cartItems, undefined, groupId, cartId, 'no-cache'),
        updateGroup(inputGroup),
      ]);

      if (savedPeriod) {
        await generateProposal({
          domainId: this.rootStore.userStore.sponsorId,
          catalogId: this._proposalItemsStore.catalog.id,
          cart,
          portfolios,
          period: savedPeriod,
          group: {
            ...group,
            agentOfRecord: group.agentOfRecord ?? { id: '', name: '', type: AssigneeType.AGENT },
          },
        });
        bpHistory.push(`/proposal-v2/${this._domainParams.periodId}?cartId=${cartId}&groupId=${groupId}`);
      }
    } finally {
      runInAction(() => {
        this._setActionLoading(false);
      });
    }
  }

  constructor(params: QuoteFlowDomainParams) {
    this._domainParams = params;

    this.flowDelegate = new QuoteFlowDelegate({
      domainParams: params,
      // The target is a legacy page that does not support the new routing system,
      // so completion redirects using redirectToRoute directly.
      nextPageId: RouteV2Id.GROUP_HUB,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const AddQuotePageStoreContext = createContext(new AddQuotePageStore({} as QuoteFlowDomainParams));

export const useAddQuotePageStore = (): AddQuotePageStore => {
  return useContext(AddQuotePageStoreContext);
};

export { AddQuotePageStore, AddQuotePageStoreContext };
